import type { Extension as FromMarkdownExtension } from "mdast-util-from-markdown";
import type { Extension, State, Tokenizer } from "micromark-util-types";
import type { Plugin } from "unified";

declare module "micromark-util-types" {
  interface TokenTypeMap {
    chatMath: "chatMath";
    chatMathData: "chatMathData";
  }
  interface Token {
    mathComplete?: boolean;
  }
}

const hasClosingDisplayMarker = (source: string, offset: number) => {
  for (let index = offset + 2; index < source.length; index++) {
    if (source[index] !== "\\") continue;
    if (source[index + 1] === "]") return true;
    index++;
  }
  return false;
};

// Block math must run before lists and paragraph breaks. Inline math runs
// before character escapes. Neither construct runs inside Markdown code.
const createMathTokenizer = (
  block: boolean,
  allowIncomplete: boolean,
  source: string,
): Tokenizer =>
  function (effects, ok, nok) {
    const interrupt = this.interrupt;
    const offset = this.now().offset;
    let closing: number;
    let dataOpen = false;
    const consume = (code: number | null) => {
      if (!dataOpen) {
        effects.enter("chatMathData");
        dataOpen = true;
      }
      effects.consume(code);
    };
    const closeData = () => {
      if (dataOpen) {
        effects.exit("chatMathData");
        dataOpen = false;
      }
    };

    const start: State = (code) => {
      // A failed multiline flow token can already affect Markdown container
      // parsing. Reject unmatched saved openers before consuming any lines.
      if (
        block &&
        !allowIncomplete &&
        (source.slice(offset, offset + 2) !== "\\[" ||
          !hasClosingDisplayMarker(source, offset))
      ) {
        return nok(code);
      }
      effects.enter("chatMath");
      consume(code);
      return opener;
    };

    const opener: State = (code) => {
      if (code !== 91 && (block || code !== 40)) return nok(code);
      closing = code === 91 ? 93 : 41;
      consume(code);
      return block && interrupt ? ok : inside;
    };

    const inside: State = (code) => {
      if (code === null) {
        if (!allowIncomplete) return nok(code);
        closeData();
        effects.exit("chatMath").mathComplete = false;
        return ok(code);
      }
      // Micromark represents LF, CR and CRLF with negative character codes.
      if (code === -5 || code === -4 || code === -3) {
        closeData();
        effects.enter("lineEnding");
        effects.consume(code);
        effects.exit("lineEnding");
        return inside;
      }
      consume(code);
      return code === 92 ? afterSlash : inside;
    };

    const afterSlash: State = (code) => {
      if (code === closing) {
        consume(code);
        closeData();
        effects.exit("chatMath").mathComplete = true;
        return block ? blockEnd : ok;
      }
      // A pair of backslashes is a LaTeX line break, not a closing marker.
      if (code === 92) {
        consume(code);
        return inside;
      }
      return inside(code);
    };
    const blockEnd: State = (code) => {
      if (code === null || code === -5 || code === -4 || code === -3)
        return ok(code);
      if (code === 32 || code === -2 || code === -1) {
        effects.enter("whitespace");
        effects.consume(code);
        effects.exit("whitespace");
        return blockEnd;
      }
      // The equation is complete. Parse the rest of this line as Markdown
      // without sending the equation back through block parsing.
      effects.enter("paragraph");
      effects.enter("chunkText", { contentType: "text" });
      return trailingText(code);
    };
    const trailingText: State = (code) => {
      if (code === null || code === -5 || code === -4 || code === -3) {
        effects.exit("chunkText");
        effects.exit("paragraph");
        return ok(code);
      }
      effects.consume(code);
      return trailingText;
    };
    return start;
  };

const createSyntax = (allowIncomplete: boolean, source: string): Extension => ({
  flow: {
    92: {
      name: "chatMathFlow",
      concrete: true,
      tokenize: createMathTokenizer(true, allowIncomplete, source),
    },
  },
  text: {
    92: {
      name: "chatMath",
      tokenize: createMathTokenizer(false, allowIncomplete, source),
    },
  },
});

const fromMarkdown: FromMarkdownExtension = {
  enter: {
    chatMath(token) {
      const source = this.sliceSerialize(token);
      this.enter(
        {
          type: "inlineCode",
          value: source,
          data: {
            hName: "span",
            hProperties: {
              "data-chat-math": source,
              "data-math-complete": Boolean(token.mathComplete),
            },
          },
        },
        token,
      );
      this.buffer();
    },
  },
  exit: {
    chatMath(token) {
      this.resume();
      this.exit(token);
    },
  },
};

export const remarkChatMath: Plugin<[{ allowIncomplete?: boolean }?]> =
  function (options = {}) {
    const data = this.data() as {
      micromarkExtensions?: Extension[];
      fromMarkdownExtensions?: FromMarkdownExtension[];
    };
    (data.fromMarkdownExtensions ??= []).push(fromMarkdown);
    const parser = this.parser;
    if (!parser) throw new Error("Chat math requires a Markdown parser");
    this.parser = (source, file) => {
      const extensions = (data.micromarkExtensions ??= []);
      extensions.push(createSyntax(options.allowIncomplete ?? false, source));
      try {
        return parser(source, file);
      } finally {
        extensions.pop();
      }
    };
  };
